import Bugsnag from "@bugsnag/js";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { FiMoreVertical } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import { BooleanParam, useQueryParam } from "use-query-params";

import { ADD_TOAST } from "../../Store/Actions/types";
import cancelReminderIcon from "../../assets/notifications/cancel-reminder.svg";
import dismissIcon from "../../assets/notifications/dismiss.svg";
import markReadIcon from "../../assets/notifications/mark-read.svg";
import markUnreadIcon from "../../assets/notifications/mark-unread.svg";
import readRemindedIcon from "../../assets/notifications/read-reminded.svg";
import readReminderIcon from "../../assets/notifications/read-reminder.svg";
import setReminderIcon from "../../assets/notifications/set-reminder.svg";
import unreadRemindedIcon from "../../assets/notifications/unread-reminded.svg";
import unreadReminderIcon from "../../assets/notifications/unread-reminder.svg";
import axiosInstance from "../../axiosInstance";
import {
  getNotificationAction,
  getNotificationActionType,
  trackEvent,
} from "../../helperFunctions";
import useMobileView from "../../hooks/useMobileView";
import { apiURL, TYPE_PSYCHIATRIST } from "../../variable";
import ToolTip from "../ToolTip";
import { Case, Switch } from "../common/ConditionalRendering";
import ConfirmationPopup from "../common/ConfirmationPopup";
import DropdownMobile from "../common/DropdownMobile";
import FlexBox from "../common/FlexBox";
import { Body2, H6, Support } from "../common/Headings";
import TimePicker from "../common/TimePicker";
import {
  DAVYS_GREY_100,
  DAVYS_GREY_200,
  DAVYS_GREY_400,
  DAVYS_GREY_700,
  DAVYS_GREY_800,
  ERROR_RED_500,
  MOSS_GREEN_100,
  MOSS_GREEN_900,
} from "../common/colors";
import { secondaryCategoryColors } from "./notificationCategories";

const HoverOption = styled(FlexBox)`
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 0.25rem;
  border: 1px solid ${DAVYS_GREY_400};
  background-color: ${DAVYS_GREY_100};
  justify-content: center;
  align-items: center;
  img {
    width: 1.5rem;
    height: 1.5rem;
  }
  :hover {
    background-color: ${DAVYS_GREY_200};
  }
`;

const HoverOptions = styled(FlexBox)`
  display: none;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: auto;
    `}

  ${HoverOption} {
    :hover {
      ${({ disabled }) =>
        disabled &&
        css`
          background-color: ${DAVYS_GREY_100};
        `}
    }
  }
`;

const Container = styled(FlexBox)`
  position: relative;
  padding: 1rem;
  flex-direction: column;
  row-gap: 0.25rem;
  background-color: ${({ isRead }) =>
    isRead ? DAVYS_GREY_100 : MOSS_GREEN_100};
  border-bottom: 1px solid ${DAVYS_GREY_400};
  cursor: pointer;
  :hover {
    ${HoverOptions} {
      display: flex;
      position: absolute;
      top: 0;
      right: 0;
      padding: 0.25rem;
      column-gap: 0.25rem;
      @media screen and (max-width: 768px) {
        display: none;
      }
    }
  }
`;

const ActionItems = styled.div``;

const MobileOptions = styled(FlexBox)`
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  top: 1rem;
  right: 1rem;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
`;

const notificationActionTypes = Object.freeze({
  MARK: "mark",
  REMIND: "remind",
  DISMISS: "dismiss",
});

const NotificationsListItem = ({ notification, source, closePopup }) => {
  const {
    id,
    title,
    is_read,
    is_muted,
    notification_category_name,
    notification_category_slug,
    description,
    created_at,
    remind_me_at,
    last_reminded_on,
  } = notification || {};

  const dispatch = useDispatch();
  const isMobile = useMobileView();
  const navigate = useNavigate();

  const isMountedRef = useRef(false);

  const user = useSelector(state => state.auth.user);
  const userType = user?.usertype;
  const userName = `${user?.firstname || ""} ${user?.lastname || ""}`;

  const [isMuted] = useQueryParam("isMuted", BooleanParam);

  const [isRead, setIsRead] = useState(is_read);
  const [reminderSetTime, setReminderSetTime] = useState(remind_me_at);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showDismissConfirmation, setShowDismissConfirmation] = useState(false);
  const [showRemoveReminderConfirmation, setShowRemoveReminderConfirmation] =
    useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [markingAsUnread, setMarkingAsUnread] = useState(false);
  const [settingReminder, setSettingReminder] = useState(false);
  const [removingReminder, setRemovingReminder] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [showMobileOptions, setShowMobileOptions] = useState(false);

  const { MARK, REMIND, DISMISS } = notificationActionTypes;

  const actionsDisabled =
    markingAsRead ||
    markingAsUnread ||
    settingReminder ||
    removingReminder ||
    dismissing;

  const textStyles = {
    bold: !isRead,
    color: isRead ? DAVYS_GREY_800 : MOSS_GREEN_900,
  };

  const commonAnalyticsPayload = {
    source,
    notif_id: id,
    type: getNotificationActionType(title),
    category: notification_category_slug,
    provider_name: userName,
    service_type: userType === TYPE_PSYCHIATRIST ? "psychiatry" : "therapy",
  };

  useEffect(() => {
    // To prevent state updates on unmounted component
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getFormattedDate = date => {
    return date ? dayjs(date).format("DD MMMM YYYY, h:mm A") : null;
  };

  const toggleTimePicker = () => {
    trackEvent({
      event: "notification_remind_click",
      payload: commonAnalyticsPayload,
    });
    setShowTimePicker(!showTimePicker);
  };

  const handleRedirection = () => {
    trackEvent({
      event: "notification_cta_click",
      payload: { ...commonAnalyticsPayload, muted: is_muted },
    });
    markAsRead();
    if (source === "dropdown") closePopup();
    getNotificationAction(isMobile, navigate, dispatch, notification, userType);
  };

  const toggleMobileOptionsDropdown = () => {
    if (actionsDisabled) return;
    setShowMobileOptions(!showMobileOptions);
  };

  const markAsRead = async (trackEventOnRead = false) => {
    if (!id) return;
    if (trackEventOnRead) {
      trackEvent({
        event: "notification_read_click",
        payload: commonAnalyticsPayload,
      });
    }
    setMarkingAsRead(true);
    try {
      const response = await axiosInstance.patch(
        `${apiURL}/providers/notifications/${id}/mark_as_read`
      );
      const isRead = response?.data?.data?.is_read;
      if (isMountedRef.current) setIsRead(isRead);
    } catch (error) {
      Bugsnag.notify(error);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Failed to mark notification as read",
          appearance: "error",
        },
      });
    } finally {
      if (isMountedRef.current) setMarkingAsRead(false);
    }
  };

  const markAsUnread = async () => {
    if (!id) return;
    trackEvent({
      event: "notification_unread_click",
      payload: commonAnalyticsPayload,
    });
    setMarkingAsUnread(true);
    try {
      const response = await axiosInstance.patch(
        `${apiURL}/providers/notifications/${id}/mark_as_unread`
      );
      const isRead = response?.data?.data?.is_read;
      setIsRead(isRead);
    } catch (error) {
      Bugsnag.notify(error);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Failed to mark notification as unread",
          appearance: "error",
        },
      });
    } finally {
      setMarkingAsUnread(false);
    }
  };

  const setReminder = async selectedReminderTime => {
    if (!id || !selectedReminderTime) return;
    trackEvent({
      event: "notification_remind_confirm",
      payload: commonAnalyticsPayload,
    });
    setSettingReminder(true);
    try {
      const response = await axiosInstance.patch(
        `${apiURL}/providers/notifications/${id}/set_reminder`,
        { reminder_date: selectedReminderTime }
      );
      const reminderTime = response?.data?.data?.remind_me_at;
      setReminderSetTime(reminderTime);
      setShowTimePicker(false);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Reminder set successfully",
          appearance: "success",
        },
      });
    } catch (error) {
      Bugsnag.notify(error);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Failed to set reminder",
          appearance: "error",
        },
      });
    } finally {
      setSettingReminder(false);
    }
  };

  const removeReminder = async () => {
    if (!id) return;
    setRemovingReminder(true);
    try {
      const response = await axiosInstance.patch(
        `${apiURL}/providers/notifications/${id}/remove_reminder`
      );
      const notificationData = response?.data?.data;
      const reminderTime = notificationData?.remind_me_at;
      setReminderSetTime(reminderTime);
      setShowRemoveReminderConfirmation(false);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Reminder cancelled successfully",
          appearance: "success",
        },
      });
    } catch (error) {
      Bugsnag.notify(error);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Failed to cancel reminder",
          appearance: "error",
        },
      });
    } finally {
      setRemovingReminder(false);
    }
  };

  const dismissNotification = async () => {
    if (!id) return;
    trackEvent({
      event: "notification_delete_confirm",
      payload: commonAnalyticsPayload,
    });
    setDismissing(true);
    try {
      const response = await axiosInstance.delete(
        `${apiURL}/providers/notifications/${id}`
      );
      const isDeleted = response?.data?.data?.is_deleted;
      setIsDismissed(isDeleted);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Notification dismissed successfully",
          appearance: "success",
        },
      });
    } catch (error) {
      Bugsnag.notify(error);
      dispatch({
        type: ADD_TOAST,
        payload: {
          msg: "Failed to dismiss notification",
          appearance: "error",
        },
      });
    } finally {
      setDismissing(false);
    }
  };

  const handleAction = actionType => {
    if (actionsDisabled) return;
    switch (actionType) {
      case MARK:
        isRead ? markAsUnread() : markAsRead(true);
        break;
      case REMIND:
        reminderSetTime
          ? setShowRemoveReminderConfirmation(true)
          : toggleTimePicker();
        break;
      case DISMISS:
        trackEvent({
          event: "notification_delete_click",
          payload: commonAnalyticsPayload,
        });
        setShowDismissConfirmation(true);
        break;
      default:
        break;
    }
  };

  let mobileOptions = [
    {
      id: "mark-status",
      label: isRead ? "Mark as unread" : "Mark as read",
      action: () => handleAction(MARK),
    },
    {
      id: "remind",
      label: reminderSetTime ? "Cancel Reminder" : "Remind me later",
      action: () => handleAction(REMIND),
    },
    {
      id: "dismiss",
      label: "Dismiss notification",
      action: () => handleAction(DISMISS),
      labelColor: ERROR_RED_500,
    },
  ];

  if (isMuted) {
    mobileOptions = mobileOptions.filter(option => option.id !== "remind");
  }

  if (isDismissed) return null;

  return (
    <Container
      className="notifications-list-item"
      isRead={isRead}
      onClick={handleRedirection}
    >
      <ActionItems onClick={e => e.stopPropagation()}>
        {isMobile && (
          <MobileOptions
            onClick={toggleMobileOptionsDropdown}
            disabled={actionsDisabled}
          >
            <FiMoreVertical color={DAVYS_GREY_800} size="1.5rem" />
            {showMobileOptions && (
              <DropdownMobile
                showBottomSheetOptions
                dismissOnClickOutside
                options={mobileOptions}
                onSelect={option => {
                  option.action();
                  setShowMobileOptions(false);
                }}
                dismissDropdown={() => setShowMobileOptions(false)}
              />
            )}
          </MobileOptions>
        )}
        {showDismissConfirmation && (
          <ConfirmationPopup
            title="Are you sure you want to dismiss this notification?"
            subtitle="Once removed, you'll not be able to access it again."
            cancelTitle="Go Back"
            confirmTitle="Dismiss"
            onConfirm={dismissNotification}
            onCancel={() => setShowDismissConfirmation(false)}
          />
        )}
        {showRemoveReminderConfirmation && (
          <ConfirmationPopup
            title="Are you sure you want to remove this reminder?"
            subtitle="Once removed, you'll not be reminded again."
            cancelTitle="Go Back"
            confirmTitle="Remove"
            onConfirm={removeReminder}
            onCancel={() => setShowRemoveReminderConfirmation(false)}
          />
        )}
        {showTimePicker && (
          <TimePicker
            title="Get reminded again on"
            closeTimePicker={toggleTimePicker}
            onTimeSelect={setReminder}
          />
        )}
        <HoverOptions disabled={actionsDisabled}>
          <HoverOption
            data-tip
            data-for={`notification-${id}-mark-read-tooltip`}
            onClick={() => handleAction(MARK)}
          >
            <img src={isRead ? markUnreadIcon : markReadIcon} alt="read icon" />
            <ToolTip id={`notification-${id}-mark-read-tooltip`} place="top">
              {isRead ? "Mark unread" : "Mark as read"}
            </ToolTip>
          </HoverOption>
          {!isMuted && (
            <HoverOption
              data-tip
              data-for={`notification-${id}-reminder-tooltip`}
              onClick={() => handleAction(REMIND)}
            >
              <img
                src={reminderSetTime ? cancelReminderIcon : setReminderIcon}
                alt="reminder icon"
              />
              <ToolTip id={`notification-${id}-reminder-tooltip`} place="top">
                {reminderSetTime ? "Remove" : "Remind me"}
              </ToolTip>
            </HoverOption>
          )}
          <HoverOption
            data-tip
            data-for={`notification-${id}-dismiss-tooltip`}
            onClick={() => handleAction(DISMISS)}
          >
            <img src={dismissIcon} alt="dismiss icon" />
            <ToolTip id={`notification-${id}-dismiss-tooltip`} place="top">
              Dismiss
            </ToolTip>
          </HoverOption>
        </HoverOptions>
      </ActionItems>
      <H6
        bold
        textTransform="uppercase"
        color={secondaryCategoryColors[notification_category_slug]}
      >
        {notification_category_name}
      </H6>
      <Body2 bold={textStyles.bold} color={textStyles.color}>
        {description}
      </Body2>
      <FlexBox wrap="wrap" columnGap="0.25rem" rowGap="0.25rem">
        <Support color={DAVYS_GREY_700}>{getFormattedDate(created_at)}</Support>
        <FlexBox columnGap="0.25rem">
          <Switch>
            <Case condition={reminderSetTime}>
              <img
                src={isRead ? readReminderIcon : unreadReminderIcon}
                alt="reminder icon"
              />
              <H6 bold={textStyles.bold} color={textStyles.color}>
                Reminder set for {getFormattedDate(reminderSetTime)}
              </H6>
            </Case>
            <Case condition={last_reminded_on}>
              <img
                src={isRead ? readRemindedIcon : unreadRemindedIcon}
                alt="reminded icon"
              />
              <H6 bold={textStyles.bold} color={textStyles.color}>
                Last reminded on {getFormattedDate(last_reminded_on)}
              </H6>
            </Case>
          </Switch>
        </FlexBox>
      </FlexBox>
    </Container>
  );
};

export default NotificationsListItem;
